import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { getPreferences } from '../utils/preferences';

// Client-side pagination. Replaces the itemsPerPage / slice / page-button
// arithmetic repeated in 20 modules.
//
//   const { currentItems, currentPage, totalPages, next, prev } = usePagination(filtered);

export interface UsePaginationResult<T> {
  currentItems: T[];
  /** Every row across all pages — what a print or export should cover. */
  items: T[];
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  setItemsPerPage: Dispatch<SetStateAction<number>>;
  next: () => void;
  prev: () => void;
  reset: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export const usePagination = <T = unknown>(
  items: T[] | undefined,
  // The parish-wide rows-per-page from Settings, read when the list mounts.
  pageSize: number = getPreferences().pageSize
): UsePaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(pageSize);

  const list = useMemo<T[]>(() => (Array.isArray(items) ? items : []), [items]);
  const totalItems = list.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Filtering down to fewer pages must not strand the user on a dead page.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const currentItems = useMemo(
    () => list.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [list, currentPage, itemsPerPage]
  );

  const next = useCallback(() => setCurrentPage((p) => Math.min(p + 1, totalPages)), [totalPages]);
  const prev = useCallback(() => setCurrentPage((p) => Math.max(p - 1, 1)), []);
  const reset = useCallback(() => setCurrentPage(1), []);

  return {
    currentItems,
    items: list,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    setItemsPerPage,
    next,
    prev,
    reset,
    isFirst: currentPage === 1,
    isLast: currentPage === totalPages
  };
};

export default usePagination;
